import { SetRefs } from '@/data/context/SidebarContext';
import useAuth from '@/data/hook/useAuth';
import useSidebar from '@/data/hook/useSidebar';
import { pathChecker } from '@/utils/pathChecker';
import {
  ChevronRightIcon,
  ArrowLeftOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'motion/react';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import SidebarItem from '../SidebarItem';
import useOptions, { Option } from './Sidebar.hooks';

const SidebarDesktop = memo(() => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('translation', {
    keyPrefix: 'sideMenu',
  });

  useEffect(() => {
    if (user === null) {
      navigate('/login');
    }
  }, [user, navigate]);

  const options = useOptions();

  const { isOpen, setSidebar, setRefs } = useSidebar();

  const isIncludedPath = useRef(pathChecker());

  useEffect(() => {
    if (isIncludedPath.current) setSidebar(false);
    return;
  }, [setSidebar]);

  const width = isOpen ? '200px' : '52px';
  return (
    <motion.div
      className="hidden sticky top-3 h-[calc(100vh-24px)] lg:block shrink-0 z-10 w-fit"
      key="sidebar-desktop"
    >
      <motion.aside
        data-testid="sidebarDesktop"
        id="sidebar"
        className="relative bg-accent h-full flex z-5 flex-col justify-between py-1 px-2.5 overflow-hidden"
        style={{
          width,
        }}
        animate={{
          width,
          transition: {
            duration: 0.15,
            ease: 'linear' as const,
          },
        }}
      >
        <div className="flex flex-col gap-[3vh]">
          <LogoSidebar isOpen={isOpen} />
          <SidebarItem
            onClick={() => setSidebar(!isOpen)}
            text={isOpen ? t('collapse') : t('expand')}
            classNames={{
              active: 'bg-transparent text-base-100 hover:bg-base-100/20',
              disable: 'bg-transparent text-base-100',
            }}
            testId="sidebarCollapseButton"
            icon={
              <MotionIcon
                className="w-5 h-5"
                animate={{ rotate: isOpen ? 180 : 0 }}
              />
            }
            isMenuOpen={isOpen}
          />
          <SidebarItens options={options} isOpen={isOpen} setRefs={setRefs} />
        </div>
        <SidebarItem
          url="/login"
          isMenuOpen={isOpen}
          onClick={logout}
          icon={<ArrowLeftOnRectangleIcon className="w-5 h-5" />}
          testId="sidebarLogout"
          text={t('leave')}
        />
      </motion.aside>
    </motion.div>
  );
});

export default SidebarDesktop;
type SidebarItensProps = {
  options: Option[];
  isOpen: boolean;
  setRefs: SetRefs;
};

function SidebarItens({ options, isOpen, setRefs }: SidebarItensProps) {
  const handleOptionsMap = useCallback(
    ({ condition, ...option }: Option) => {
      if (!option.roles)
        return (
          <SidebarItem
            testId={option.testId}
            key={option.title}
            icon={option.icon}
            isMenuOpen={isOpen}
            text={option.title}
            url={option.url}
            condition={condition}
          />
        );

      return (
        <ComponentGuard key={option.title} roles={option.roles}>
          <SidebarItem
            testId={option.testId}
            icon={option.icon}
            isMenuOpen={isOpen}
            text={option.title}
            url={option.url}
            condition={condition}
            setRef={setRefs[option.testId]}
          />
        </ComponentGuard>
      );
    },
    [isOpen, setRefs],
  );
  return (
    <div className="flex flex-col gap-5">{options.map(handleOptionsMap)}</div>
  );
}

type LogoSidebarProps = {
  isOpen: boolean;
};

function LogoSidebar({ isOpen }: LogoSidebarProps) {
  return (
    <div className="relative flex items-center justify-start px-2 py-2 h-14">
      {!isOpen ? (
        <motion.img
          key="short-logo"
          className="max-w-8 -translate-x-1.5"
          src="/images/CtrlPlayShortLogoWhite.png"
          alt="Ctrl+Play - Logo compacto"
          loading="lazy"
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
        />
      ) : (
        <motion.img
          key="full-logo"
          className="max-w-[120px] mt-7 self-center mx-auto"
          src="/images/CtrlPlayFullLogoWhite.png"
          alt="Ctrl+Play - Logo completo"
          loading="lazy"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2, delay: 0.1 }}
        />
      )}
    </div>
  );
}

const MotionIcon = motion.create(ChevronRightIcon);
